/* Status for Overview Tab */
import {
  AR_V2,
  CORE_AMZL,
  DEFAULT_CAPABILITIES,
  ROUTE_VIEW,
  SSD,
} from "./util/Constants";
import { isARorSSD } from "./util/Util";
import {
  getPicklistStatus,
  isRouteAtRisk as isRouteAtRiskUtil,
} from "../RouteStatus/utils/Util";
import { getRouteStatus } from "../RouteStatus/utils/EntityStatusManager";

export const PENDING_PICK = "Pending Pick";
export const PENDING_STAGE = "Pending Stage";
export const PENDING_DEPART = "Pending Depart";
export const INTERIM_STAGED = "Staged-Interim";
export const DEPARTED = "Departed";
export const ROUTES_AT_RISK = "At Risk";
export const ROUTES_TO_BE_CUT = "To be cut";

/* Route Status received from Backend */
export const ASSIGNABLE = "Assignable";
export const NOT_READY = "Not Ready"; // means Pending Stow for AMXL
export const IN_PROGRESS = "In Progress";
export const DELAYED_PICK = "Delayed Pick";
export const PICKED = "Picked";
export const STAGED = "Staged";
export const PRE_STAGED = "Pre Staged";
export const PARTIALLY_DEPARTED = "Departed (partial)";
export const PARTIALLY_STAGED = "Staged (partial)";

/* Depart Tab Status */
export const NOT_DEPARTED = "Not Departed";
export const NOT_STAGED = "Not Staged";

export const NOT_ASSIGNED = "Not Assigned";
export const READY = "Ready";

function isStagingComplete(route, capability) {
  /* Sidelined routes are STAGED */
  return route.routeStatus === STAGED || route.routeStatus === PARTIALLY_STAGED;
}

function isPendingStage(route, capability, stationConfig) {
  if (DEFAULT_CAPABILITIES.includes(capability)) {
    if (stationConfig.pickStageDecoupleEnabled) {
      return route.routeStatus === PRE_STAGED;
    } else {
      return (
        (isARorSSD(capability) && route.routeStatus === IN_PROGRESS) ||
        isPickingComplete(route, capability)
      );
    }
  }
  return false;
}

/**
 * Checks if route is partial staged or partially departed.
 * @param route
 * @return {boolean}
 */
export function isPartial(route, stationConfig, capability) {
  // NOTE: for ARv2, route.pickProgressV2.completedCount remains 0, read status from API
  // NOTE: for ARv2, stationConfig is null in old UI so check capability before reading it
  // NOTE: For STAG, return false, as there are no items in SPS responsible for tracking workflow progress.
  // TODO : build a solution to retrieve item level progress.
  if (stationConfig.stagenabled) {
    return false;
  } else if (
    capability !== AR_V2 &&
    route.routeStatus === STAGED &&
    !stationConfig.pickStageDecoupleEnabled &&
    !isARorSSD(capability)
  ) {
    return (
      route.pickProgressV2 &&
      route.pickProgressV2.totalCount &&
      route.pickProgressV2.completedCount < route.pickProgressV2.totalCount
    );
  } else {
    return (
      route.routeStatus && route.routeStatus.toLowerCase().includes("partial")
    );
  }
}

/**
 * Returns priority of status for visual progress grid.
 * @param overviewStatus
 * @return {number}
 */
export function getOverviewStatusPriority(overviewStatus) {
  switch (overviewStatus) {
    case ASSIGNABLE:
      return 1;
    case PENDING_PICK:
      return 2;
    case PENDING_STAGE:
      return 3;
    case PENDING_DEPART:
      return 4;
    case DEPARTED:
      return 5;
    default:
      return 0;
  }
}

function isPickingComplete(route, capability) {
  return (
    route.routeStatus === PICKED ||
    (capability === AR_V2 &&
      (route.routeStatus === PARTIALLY_STAGED || route.routeCut)) ||
    (route.pickProgressV2 &&
      route.pickProgressV2.totalCount > 0 &&
      route.pickProgressV2.completedCount === route.pickProgressV2.totalCount)
  );
}

/**
 * Updates route status to assignable if route is unassigned.
 * Applicable for AMZL non parallel pick sites only.
 *
 * @param capability
 * @param routes
 * @returns updated routes
 */
export function handleUnassignedRoutes(capability, routes, stationConfig) {
  if (
    CORE_AMZL.includes(capability) &&
    !stationConfig.amxl &&
    !stationConfig.parallelPickEnabled
  ) {
    routes.forEach((route) => {
      if (route.routeStatus === IN_PROGRESS && !route.activeAssignee) {
        route.routeStatus = ASSIGNABLE;
      }
    });
  }
  return routes;
}

export function getOverallRouteStatus(route, capability, stationConfig) {
  if (
    route.routeStatus === DEPARTED ||
    route.routeStatus === PARTIALLY_DEPARTED
  ) {
    return DEPARTED;
  } else if (isStagingComplete(route, capability)) {
    return PENDING_DEPART;
  } else if (
    isPendingStage(route, capability, stationConfig) ||
    (capability === AR_V2 && isPickingComplete(route, capability))
  ) {
    return PENDING_STAGE;
  } else if (route.routeStatus === IN_PROGRESS) {
    return PENDING_PICK;
  } else if (
    route.routeStatus === NOT_READY &&
    (stationConfig.amxl ||
      isARorSSD(capability) ||
      stationConfig.drsPickStageVisibilityEnabled)
  ) {
    return NOT_READY;
  } else {
    return ASSIGNABLE;
  }
}

export function getRouteCountByStatus(status, routeList) {
  let result = 0;
  routeList.forEach((route) => {
    if (route.overviewStatus === status) {
      result = result + 1;
    }
  });
  return result;
}

/**
 * Returns count of routes at risk
 *
 * @param routeList
 * @param sprPackageCountThreshold
 * @param sprTimeLimitThreshold - max allowed time diff between driver arrival and picking complete
 * @returns {number|*}
 */
export function getRouteAtRiskCount(
  routeList,
  sprPackageCountThreshold,
  sprTimeLimitThreshold,
) {
  if (!Array.isArray(routeList)) {
    return 0;
  }
  return routeList.filter((route) =>
    isRouteAtRisk(route, sprPackageCountThreshold, sprTimeLimitThreshold),
  ).length;
}
/**
 * Returns count of routes at risk for SSD
 *
 * @param routeList
 * @param cptTimeLimitThreshold - critical pull time for the route
 * @returns {number|*}
 */
export function getRouteAtRiskCountForSSD(routeList, cptTimeLimitThreshold) {
  if (!Array.isArray(routeList)) {
    return 0;
  }
  return routeList.filter((route) =>
    isRouteAtRiskForSSD(route, cptTimeLimitThreshold),
  ).length;
}

/**
 * Returns count of routes at risk using enriched metric calculation
 *
 * @param routeList
 * @returns {number}
 */
export function getRouteAtRiskCountForStageByTimeSla(routeList) {
  if (!Array.isArray(routeList)) {
    return 0;
  }
  return routeList.filter((route) => {
    const transformedRoute = {
      ...route,
      routeStatus: getRouteStatus(route),
      picklists: route.picklists
        ? route.picklists.map((picklist) => ({
            ...picklist,
            status: getPicklistStatus(picklist),
          }))
        : [],
    };
    return isRouteAtRiskUtil(transformedRoute);
  }).length;
}

/**
 * Calculates the at-risk route count based on station configuration and metrics
 * @param {Object} config - Configuration object
 * @param {Array} config.routeList - List of routes to analyze
 * @param {boolean} config.isAtRiskMetricEnrichmentEnabled - Flag for enriched metrics
 * @param {boolean} config.isStationTabularSSD - Flag for SSD station type
 * @param {number} config.cptTimeLimit - CPT time limit for SSD stations
 * @param {number} config.sprPackageCount - SPR package count threshold
 * @param {number} config.sprTimeLimit - SPR time limit threshold
 * @returns {number} Count of at-risk routes
 */
export function evaluateRouteAtRiskCount({
  routeList,
  isAtRiskMetricEnrichmentEnabled,
  isStationTabularSSD,
  cptTimeLimit,
  sprPackageCount,
  sprTimeLimit,
}) {
  if (isAtRiskMetricEnrichmentEnabled) {
    return getRouteAtRiskCountForStageByTimeSla(routeList);
  } else if (isStationTabularSSD) {
    return getRouteAtRiskCountForSSD(routeList, cptTimeLimit);
  }
  return getRouteAtRiskCount(routeList, sprPackageCount, sprTimeLimit);
}

/**
 * Check if route is stalled or at risk
 *
 * @param route
 * @param sprPackageCountThreshold
 * @param sprTimeLimitThreshold
 * @param stationConfig
 */
export function isRiskAtRiskOrStalled(
  route,
  sprPackageCountThreshold,
  sprTimeLimitThreshold,
  stationConfig,
) {
  const atRisk = isRouteAtRisk(
    route,
    sprPackageCountThreshold,
    sprTimeLimitThreshold,
  );
  return atRisk || isRouteStalled(route, stationConfig);
}

/**
 * Checks if driver is arriving within time threshold and route is not assigned yet, plus its high SPR.
 *
 * @param route
 * @param sprPackageCountThreshold
 * @param sprTimeLimitThreshold
 * @return {boolean}
 */
export function isRouteAtRisk(
  route,
  sprPackageCountThreshold,
  sprTimeLimitThreshold,
) {
  const maxDiffTimeAllowed = sprTimeLimitThreshold * 60000; // convert into milliseconds
  const timeDiff = route.criticalPullTime - new Date().getTime();
  const isTimeCrunch = timeDiff < maxDiffTimeAllowed;
  const isHighSPR = route.routeSPR >= sprPackageCountThreshold;
  const isRouteStatusRisky = route.overviewStatus === ASSIGNABLE;
  return isTimeCrunch && isHighSPR && isRouteStatusRisky;
}

/**
 * Checks if driver is arriving within time threshold and route is not assigned yet.
 *
 * @param route
 * @param cptTimeLimitThreshold
 * @return {boolean}
 */
export function isRouteAtRiskForSSD(route, cptTimeLimitThreshold) {
  const maxDiffTimeAllowed = cptTimeLimitThreshold * 60000; //  convert into milliseconds
  const timeDiff = route.criticalPullTime - new Date().getTime();
  const isTimeCrunch = timeDiff < maxDiffTimeAllowed;
  const isRouteStatusRisky = route.overviewStatus === ASSIGNABLE;
  return isTimeCrunch && isRouteStatusRisky;
}

/**
 * Returns count of routes at risk for ARv2
 *
 * @param routeList
 * @returns {number|*}
 */
export function getRouteAtRiskCountARv2(routeList) {
  if (!Array.isArray(routeList)) {
    return 0;
  }
  return routeList.filter((route) => isRouteAtRiskARv2(route)).length;
}

/**
 * Checks if the route is in progress status and the pick duration is more than or equal to 60 mins
 *
 * @param route
 * @return {boolean}
 */
export function isRouteAtRiskARv2(route) {
  const isRouteStatusRisky = route.overviewStatus === PENDING_PICK;
  if (isRouteStatusRisky) {
    const maxDurationAllowed = 60 * 60; // convert 60 minutes into seconds
    return route.pickDurationInSeconds >= maxDurationAllowed;
  }
  return false;
}

/**
 * Checks if a route is incomplete and it has no in-progress picklist but some picklists are pending for pick.
 * Note: this is for parallel pick only
 *
 * @param route
 * @param stationConfig
 * @return {boolean}
 */
export function isRouteStalled(route, stationConfig) {
  if (
    !stationConfig.parallelPickEnabled ||
    route.rswsStatus !== IN_PROGRESS ||
    !Array.isArray(route.picklists) ||
    !route.picklists.length
  ) {
    return false;
  }
  const inProgress = route.picklists.findIndex(
    (picklist) => picklist.status === IN_PROGRESS,
  );
  const notAssigned = route.picklists.findIndex(
    (picklist) => picklist.status === NOT_ASSIGNED,
  );
  return inProgress === -1 && notAssigned >= 0; //No in-progress but at-least one assignable picklist.
}

/**
 * Returns pick status for picklists
 *
 * @param picklist object in response from RSWS
 * @returns picking status
 */
export function getPickStatusForPicklist(picklist) {
  if (picklist.status === ASSIGNABLE) {
    return NOT_ASSIGNED;
  } else {
    return picklist.status;
  }
}

export function getRouteFilterByMetric(
  metric,
  filters,
  stationConfig,
  capability,
) {
  if (metric === DEPARTED) {
    return {
      status: [DEPARTED, PARTIALLY_DEPARTED],
    };
  } else if (metric === PENDING_DEPART) {
    return {
      status: [READY],
    };
  } else if (metric === PENDING_STAGE) {
    if (stationConfig.pickStageDecoupleEnabled) {
      return {
        status: [PRE_STAGED],
        viewType: ROUTE_VIEW,
      };
    } else if (capability === AR_V2) {
      return {};
    } else {
      return {
        status: [READY],
        viewType: ROUTE_VIEW,
      };
    }
  } else if (metric === PENDING_PICK) {
    return {
      routeStatus: [IN_PROGRESS],
      sprPackageCount: filters.sprPackageCount,
      sprTimeLimit: filters.sprTimeLimit,
    };
  } else if (metric === ASSIGNABLE) {
    if (isARorSSD(capability)) {
      return {
        status: [READY],
        viewType: ROUTE_VIEW,
      };
    }
    return {
      routeStatus: [NOT_ASSIGNED],
      sprPackageCount: filters.sprPackageCount,
      sprTimeLimit: filters.sprTimeLimit,
    };
  } else if (metric === ROUTES_AT_RISK) {
    if (capability === AR_V2) {
      return {
        routeStatus: [DELAYED_PICK],
      };
    } else if (capability === SSD) {
      return {
        atRisk: [true],
        cptTimeLimit: filters.cptTimeLimit,
      };
    }
    return {
      atRisk: [true],
      sprPackageCount: filters.sprPackageCount,
      sprTimeLimit: filters.sprTimeLimit,
    };
  } else if (metric === ROUTES_TO_BE_CUT) {
    // set values for showing Routes to be cut in Pick tab
    return {
      routeStatus: [IN_PROGRESS],
      rcTimeLimit: filters.rcTimeLimit,
      rcPackageCount: filters.rcPackageCount,
    };
  } else {
    return {
      status: [],
    };
  }
}

export function getAmxlRouteStatus(route) {
  if (route.stowProgress == null && route.departProgress == null) {
    return ASSIGNABLE;
  } else if (
    route.departProgress != null &&
    route.departProgress.completedCount === route.departProgress.totalCount
  ) {
    return DEPARTED;
  } else if (
    (route.departProgress != null && 0 < route.departProgress.completedCount) ||
    (route.stowProgress != null &&
      route.stowProgress.completedCount === route.stowProgress.totalCount)
  ) {
    return PENDING_DEPART;
  } else if (
    route.stowProgress != null &&
    route.stowProgress.completedCount < route.stowProgress.totalCount
  ) {
    return NOT_READY;
  } else {
    return ASSIGNABLE;
  }
}

export function isRouteStaged(route) {
  return route.overviewStatus === PENDING_DEPART;
}



// WEBPACK FOOTER //
// ./src/components/outboundAmzl/RouteStatusManager.js